<template>
    <teleport :to="teleportTarget">
        <!-- Dark Layer Transition -->
        <transition
        name="overlay-modal-animation"
        appear
        @after-enter="showModalWrapper = true"
        @after-leave="emit('hideMe')">
            <div
            v-if="showMe"
            class="overlay-modal-wrapper"
            :class="{'dont-close-side-modals': preventDetailsModalClose, 'second-overlay-teleport': applySecondLayer}">
                <!-- White Detail Content Transition -->
                <transition
                name="side-modal-animation"
                appear
                @after-leave="showMe = false"
                @after-enter="emit('allowPortalElementsToBeRender', true)">
                    <div
                    v-if="showModalWrapper"
                    v-click-outside="closeModal"
                    class="animation-div-wrapper"
                    :class="[{ 'mobile': getMobileAppState, 'up-bellow-entry': upBellowEntry }, sizeClass]"
                    :style="{'padding-bottom': getKeyboardHeight }">
                        <div
                        ref="genericContentWraperRef"
                        class="generic-content-wrapper"
                        :class="{ 'mobile': getMobileAppState }"
						:style="{ 'background-image': `linear-gradient(${headerColor} 25%, var(--secondary-light-3) 10%)`}"
                        @scroll="valueScrolled = $event.target.scrollTop">
                            <!-- Header -->
                            <modal-side-header
                            :upBellowEntry="upBellowEntry"
                            :invertTitleSubtitle="invertTitleSubtitle"
                            :featureLikeHeader="featureLikeHeader"
                            :style="{ 'background-color': parsedHeaderColor }"
                            :withHeaderColor="headerColor"
                            :title="title"
                            :titleMask="titleMask"
                            :negativeTitle="negativeTitle"
                            :subTitle="subTitle"
                            :subTitleIcon="subTitleIcon"
                            :icon="icon"
                            :valueScrolled="valueScrolled"
                            :headerLeftButtonText="headerLeftButtonText"
                            :emitLeftButtonText="emitLeftButtonText"
                            :buttonsTopData="buttonsTopData"
                            :tabs="tabs"
                            :showSearchSlot="showSearchSlot"
                            :helperTextTitle="helperTextTitle"
                            @tabClicked="emit('tabClicked', $event)"
                            @buttonLeftClicked="emit('buttonLeftClicked')"
                            @backOrCloseClicked="[emit('backOrCloseClicked'), showModalWrapper = false]">
                                <!-- Slot for search -->
                                <template v-if="showSearchSlot" #top-search-area-slot><slot name="top-search-area-slot"/></template>
                            </modal-side-header>
                            <!-- Content -->
                            <div
                            class="modal-content-wrapper"
                            :class="{ 'with-footer': showFooter, 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                                <!-- Error State -->
                                <empty-state-error v-if="applyErrorState" :message="errorStateMessage"/>
                                <slot v-else></slot>
                            </div>
                        </div>
                        <!-- Footer -->
                        <navigation-footer
                        usedInDetails
                        :buttonsParams="buttonsFooterData"
                        :showFooter="showFooter"
                        :buttonFullWidth="buttonFullWidth"/>
                    </div>
                </transition>
                <!-- Teleport for floating scroll -->
                <div id="side-modal-floating-scroll-teleport"/>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import NavigationFooter from '@/components/generic/navigation/Footer'
import ModalSideHeader from '@/components/generic/modals/side/header/Base'
import EmptyStateError from '@/components/generic/emptyStates/Error'

/* Vue */
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'
import { scrollTo } from '@/assets/scripts/scrollAnimation'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['hideMe', 'allowPortalElementsToBeRender', 'tabClicked', 'clearScrollToValue', 'buttonLeftClicked', 'backOrCloseClicked'])

/* Props
@upBellowEntry {Boolean} - prop to apply class to animate modal from down to top (instead os right-left default animation)
@invertTitleSubtitle {Boolean} - prop to invert order of Title and Subtitle
@featureLikeHeader {Boolean} - apply styles like a feature wrapper header
@headerColor {String} - color for header
@headerClasses {String} - prop to apply specific header class, classes being mapped [payments-services]
@sizeClass {String} - possible values [medium(560), big(640), big-plus(700), giant(900)], if not passed we will apply standard with 450
@title {String} - text to display in our title
@titleMask {String} - add mask on our title
@negativeTitle {Boolean} - prop to apply negative color to title
@subTitle {String} - text to display under our title
@icon: {String} - iconName+path(if needed) to display
@buttonsTopData {Object} -
@tabs {Array} - tabs to display on header
@showSearchSlot {Boolean} - prop to apply slot for search
@centerLoader {Boolean} - prop to add class to center elements in content
@showFooter {Boolean} - prop to control display of footer
@buttonsFooterData {Array} - array of buttons to display on detail footer
@buttonFullWidth {Boolean}
@noPaddingBottom {Boolean} - prop to remove padding bottom of content
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
@headerLeftButtonText {String} - text to display in left button
@emitLeftButtonText {Boolean} - prop to tell which emit we want to do when closing left button on top
@applySecondLayer {Boolean} - prop to apply second-overlay-teleport styles
@preventDetailsModalClose{Boolean} - prop to prevent details modal close trigger
@applyErrorState {Boolean} - prop to apply Error State
@errorStateMessage {String} - message to display on errorState
@helperTextTitle {Boolean} - prop to apply helper text title styles
*/
const props = defineProps({
    upBellowEntry: Boolean,
    invertTitleSubtitle: Boolean,
    featureLikeHeader: Boolean,
    headerColor: String,
    headerClasses: String,
    sizeClass: String,
    title: String,
    titleMask: String,
    negativeTitle: Boolean,
    subTitle: String,
    subTitleIcon: String,
    icon: String,
    buttonsTopData: Object,
    tabs: Array,
    showSearchSlot: Boolean,
    centerLoader: Boolean,
    showFooter: Boolean,
    buttonsFooterData: Array,
    buttonFullWidth: Boolean,
    noPaddingBottom: Boolean,
    triggerClose: Boolean,
    headerLeftButtonText: String,
    emitLeftButtonText: Boolean,
    applySecondLayer: Boolean,
    preventDetailsModalClose: Boolean,
    applyErrorState: Boolean,
    errorStateMessage: String,
    scrollValue: Number,
    helperTextTitle: Boolean
})

const { updatePreventMobileFeatureBack, updateSideModalScroll } = mapActions()
const { getMobileAppState, getDeviceInUse, getPreventMobileSideModalBackState, getKeyboardHeight, getAppThemeMode } = mapGetters()

const showMe = ref(true)
const showModalWrapper = ref(false)

const teleportTarget = computed(() => {
    return props.applySecondLayer ? '#second-side-layer-overlay-teleport' : '#side-layer-overlay-teleport'
})

const genericContentWraperRef = ref('')
const valueScrolled = ref(0)

watch(() => props.triggerClose, () => {
    emit('allowPortalElementsToBeRender', false)
    showModalWrapper.value = false
})

watch(() => props.scrollValue, (newVal, oldVal) => {
    if (newVal || newVal === 0) {
        scrollTo(genericContentWraperRef.value, props.scrollValue, null, 500, 'vertical')
        emit('clearScrollToValue')
    }
})

const parsedHeaderColor = computed(() => {
    if (!props.headerColor) return null
	if (!props.headerColor.includes('_')) return props.headerColor

    return getAppThemeMode.value === 'dark' ? props.headerColor.split('_')[1] : props.headerColor.split('_')[0]
})

const validateIfClose = $event => {
    const className = 'dont-close-side-modals'
    const className2 = 'dont-close-details'
    const className3 = 'is-download-click'
    const className4 = 'dz-hidden-input'

    const eventHasClass = ($event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className))
    /* Download Click class */
    const eventHasClass3 = ($event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className3))
    /* Uploader Class */
    const eventHasClass4 = ($event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className4))
    if (validateIfParentExists($event.target, className3) || eventHasClass3 || eventHasClass4) return true

    return (validateIfParentExists($event.target, className) || validateIfParentExists($event.target, className2) || eventHasClass) && !props.applySecondLayer
}

const closeModal = $event => {
    if (validateIfClose($event)) return
    /* If not, then we close our sideModal */
    emit('allowPortalElementsToBeRender', false)
    showModalWrapper.value = false
}

const updateScrollValue = $event => {
	updateSideModalScroll($event.target.scrollTop)
}

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
	updateSideModalScroll(0)
})

const onBackKeyDown = () => {
    if (getPreventMobileSideModalBackState.value) return
    showModalWrapper.value = false
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.overlay-modal-wrapper {
    @include sideLayerOverlayTeleportStyles;
    @include overlayBaseAnimation;

    &.second-overlay-teleport {
        @include secondSidelayerOverlayTeleportStyles;
    }

    .animation-div-wrapper {
        &:not(.up-bellow-entry) {
            @include sideModalAnimation;
        }

        &.up-bellow-entry {
            @include sideModalUpBellowAnimation;
        }

        width: 560px;
        max-width: 90%;
        height: 100%;

        position: relative;
        border-radius: 16px 0 0 16px;
        overflow: hidden;

        &.medium {
            width: 560px;
        }

        &.big {
            width: 640px;
        }

        &.big-plus {
            width: 700px;
        }

        &.giant {
            width: 1045px;
        }

        &.mobile {
            max-width: 100%;
            border-radius: 0;

            .generic-content-wrapper {
                border-radius: 0;
            }

            :deep(.footer-wrapper) {
                padding: 0 16px;
            }
        }

        .generic-content-wrapper {
            width: 100%;
            height: 100%;

            display: flex;
            flex-direction: column;

            background-color: var(--secondary-light-4);
            box-shadow: 0px 32px 64px rgba(16, 24, 40, 0.14);
            border-radius: 16px 0 0 16px;
            position: relative;

            &:not(.mobile) {
                @include generic-scrollbar(transparent, var(--primary));
            }

            .modal-content-wrapper {
                width: 100%;
                height: auto;
                padding: 2px 24px 16px 24px;

                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                flex: 1;

                z-index: 1;
				background-color: var(--secondary-light-4);

                :deep(.group-wrapper) {
                    width: 100%;
                    margin-bottom: 24px;

                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    flex-shrink: 0;
                    gap: 24px;
                }

                :deep(.error-state-wrapper) {
                    width: 100%;
                    height: calc(100% - 24px);
                }

                :deep(.error-state-wrapper) {
                    width: 100%;
                    height: calc(100% - 24px);
                }

                &.with-footer {
                    padding-bottom: 104px;
                }
            }

            &.mobile {
                overflow: auto;
                overflow-x: hidden;

                .modal-content-wrapper {
                    padding: 2px 0 16px 0;

                    &.mobile-status-bar-ios {
                        padding-bottom: calc(16px + constant(safe-area-inset-bottom)); /* iOS 11.0 */
                        padding-bottom: calc(16px + env(safe-area-inset-bottom)); /* iOS 11.2 */
                    }

                    &.with-footer {
                        padding-bottom: 72px;

                        &.mobile-status-bar-ios {
                            padding-bottom: calc(72px + constant(safe-area-inset-bottom)); /* iOS 11.0 */
                            padding-bottom: calc(72px + env(safe-area-inset-bottom)); /* iOS 11.2 */
                        }

                    }

                    :deep(.group-wrapper) {
                        > * {
                            &:not(.prevent-auto-margins) {
                                width: calc(100% - 32px);
                                margin-left: auto;
                                margin-right: auto;
                            }
                        }
                    }

                    :deep(.favorite-button-wrapper) {
                        width: calc(100% - 32px);
                        margin: 0 auto 24px auto;
                    }

                    :deep(.filters-container) {
                        width: calc(100% - 32px);
                        margin-left: auto;
                        margin-right: auto;
                    }
                }
            }
        }

        :deep(.footer-wrapper) {
            z-index: 1;
            padding: 0 24px;

            .buttons-container {
                max-width: unset;
            }
        }
    }
}
</style>
